import { useCallback, useEffect, useMemo, useState } from "react";
import api, { downloadFile } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { fmtMoney, balanceClass, todayStr, thisMonth, buildShiftMaps } from "@/lib/format";
import { PrintableStatement } from "@/components/PrintableStatement";
import { SettlementSlipBody, buildSettlementShareText } from "@/components/SettlementSlip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { DownloadSimple, FileText } from "@phosphor-icons/react";

export default function Reports() {
  const [period, setPeriod] = useState("monthly");
  const [month, setMonth] = useState(thisMonth());
  const [weekStart, setWeekStart] = useState(todayStr());
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    const start = period === "monthly" ? month : weekStart;
    api.get(`/reports?period=${period}&start=${start}`).then((r) => setData(r.data));
  }, [period, month, weekStart]);
  useEffect(() => { load(); }, [load]);

  const shifts = data?.shifts || [];
  const maps = useMemo(() => buildShiftMaps(shifts), [shifts]);

  // Per-employee report: same period window as the table above, one labourer.
  const [employees, setEmployees] = useState([]);
  const [empId, setEmpId] = useState("");
  const [empData, setEmpData] = useState(null);
  const [showEmpReport, setShowEmpReport] = useState(false);
  useEffect(() => { api.get("/employees").then((r) => setEmployees(r.data)); }, []);

  const empWindow = data ? { start: data.window_start, end: data.window_end } : null;
  const empName = employees.find((e) => e.id === empId)?.name;

  const viewEmpReport = () => {
    if (!empId || !empWindow) return;
    api.get(`/employees/${empId}/settlement?start=${empWindow.start}&end=${empWindow.end}`)
      .then((r) => { setEmpData(r.data); setShowEmpReport(true); });
  };

  return (
    <div className="p-4 md:p-8 max-w-[1200px] mx-auto w-full space-y-6">
      <PageHeader title="Reports" subtitle="Weekly and monthly totals with running balances.">
        {period === "monthly" && (
          <Button data-testid="btn-report-export" className="bg-slate-900 hover:bg-slate-800 gap-2"
            onClick={() => downloadFile(`/export/excel?month=${month}`, `attendance_${month}.xlsx`)}>
            <DownloadSimple size={16} weight="bold" /> Export Excel
          </Button>
        )}
      </PageHeader>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 flex flex-wrap items-end gap-4">
        <div className="inline-flex rounded-md border border-slate-300 overflow-hidden">
          {["monthly", "weekly"].map((p) => (
            <button key={p} data-testid={`period-${p}`} onClick={() => setPeriod(p)}
              className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${
                period === p ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
              }`}>{p}</button>
          ))}
        </div>
        {period === "monthly" ? (
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Month</Label>
            <Input data-testid="report-month" type="month" className="w-44 font-data" value={month} onChange={(e) => setMonth(e.target.value)} />
          </div>
        ) : (
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Week starting</Label>
            <Input data-testid="report-week" type="date" className="w-44 font-data" value={weekStart} onChange={(e) => setWeekStart(e.target.value)} />
          </div>
        )}
        {data && <span className="font-data text-sm text-slate-500 ml-auto">{data.label}</span>}
      </div>

      {/* Per-employee report for the same period */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 flex flex-wrap items-end gap-3">
        <div className="space-y-1 min-w-[200px]">
          <Label className="text-xs text-slate-500">Per-labourer report</Label>
          <Select value={empId} onValueChange={setEmpId}>
            <SelectTrigger data-testid="emp-report-select"><SelectValue placeholder="Select a labourer" /></SelectTrigger>
            <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button data-testid="btn-emp-report-view" variant="outline" disabled={!empId || !data}
          onClick={viewEmpReport} className="gap-2">
          <FileText size={16} /> View / Print Report
        </Button>
        {data && <span className="text-xs text-slate-400">Covers {data.label.replace(/^Month: |^Week: /, "")}</span>}
      </div>

      {data && (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="report-table">
            <thead>
              <tr className="bg-slate-100 text-xs uppercase tracking-widest text-slate-500">
                <th className="text-left px-4 py-3 font-semibold">Labourer</th>
                <th className="text-center px-3 py-3 font-semibold">Sex</th>
                {shifts.map((s) => <th key={s.id} className="text-center px-3 py-3 font-semibold">{maps.label[s.id]}</th>)}
                <th className="text-center px-3 py-3 font-semibold">Abs</th>
                <th className="text-right px-4 py-3 font-semibold">Earned</th>
                <th className="text-right px-4 py-3 font-semibold">Lent</th>
                <th className="text-right px-4 py-3 font-semibold">Paid</th>
                <th className="text-right px-4 py-3 font-semibold">Balance</th>
                <th className="text-right px-4 py-3 font-semibold text-emerald-700">AC Earned</th>
                <th className="text-right px-4 py-3 font-semibold text-emerald-700">AC Advance</th>
                <th className="text-right px-4 py-3 font-semibold text-emerald-700">AC Lend</th>
                <th className="text-right px-4 py-3 font-semibold text-emerald-700">AC Balance</th>
              </tr>
            </thead>
            <tbody className="font-data">
              {data.rows.map((r) => (
                <tr key={r.employee_id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2.5 text-left font-sans font-medium text-slate-800">{r.name}</td>
                  <td className="px-3 py-2.5 text-center text-slate-500">{r.gender}</td>
                  {shifts.map((s) => <td key={s.id} className="px-3 py-2.5 text-center">{r.shift_counts[s.id] || 0}</td>)}
                  <td className="px-3 py-2.5 text-center text-rose-600">{r.absent}</td>
                  <td className="px-4 py-2.5 text-right">{fmtMoney(r.period_earned)}</td>
                  <td className="px-4 py-2.5 text-right text-orange-600">{fmtMoney(r.period_lend)}</td>
                  <td className="px-4 py-2.5 text-right">{fmtMoney(r.period_paid)}</td>
                  <td className={`px-4 py-2.5 text-right ${balanceClass(r.balance)}`}>{fmtMoney(r.balance)}</td>
                  <td className="px-4 py-2.5 text-right text-emerald-700">{fmtMoney(r.arecanut_period_earned)}</td>
                  <td className="px-4 py-2.5 text-right text-emerald-700">{fmtMoney(r.arecanut_period_advance)}</td>
                  <td className="px-4 py-2.5 text-right text-emerald-700">{fmtMoney(r.arecanut_period_lend)}</td>
                  <td className={`px-4 py-2.5 text-right ${balanceClass(r.arecanut_balance)}`}>{fmtMoney(r.arecanut_balance)}</td>
                </tr>
              ))}
              {data.rows.length === 0 && (
                <tr><td colSpan={shifts.length + 11} className="px-4 py-10 text-center text-slate-500 font-sans">No data for this period.</td></tr>
              )}
            </tbody>
            {data.rows.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-slate-300 bg-slate-50 font-data font-semibold">
                  <td className="px-4 py-3 text-left font-sans" colSpan={2}>Totals</td>
                  <td colSpan={shifts.length}></td>
                  <td className="px-3 py-3 text-center text-rose-600">{data.totals.absent}</td>
                  <td className="px-4 py-3 text-right" data-testid="total-earned">{fmtMoney(data.totals.earned)}</td>
                  <td className="px-4 py-3 text-right text-orange-600" data-testid="total-lend">{fmtMoney(data.totals.lend)}</td>
                  <td className="px-4 py-3 text-right" data-testid="total-paid">{fmtMoney(data.totals.paid)}</td>
                  <td className={`px-4 py-3 text-right ${balanceClass(data.totals.balance)}`} data-testid="total-balance">{fmtMoney(data.totals.balance)}</td>
                  <td className="px-4 py-3 text-right text-emerald-700">{fmtMoney(data.totals.arecanut_earned)}</td>
                  <td className="px-4 py-3 text-right text-emerald-700">{fmtMoney(data.totals.arecanut_advance)}</td>
                  <td className="px-4 py-3 text-right text-emerald-700">{fmtMoney(data.totals.arecanut_lend)}</td>
                  <td className={`px-4 py-3 text-right ${balanceClass(data.totals.arecanut_balance)}`}>{fmtMoney(data.totals.arecanut_balance)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {empData && (
        <PrintableStatement open={showEmpReport} onClose={() => setShowEmpReport(false)} testid="emp-report"
          shareText={() => buildSettlementShareText(empName, empData)}
          onExcel={() => downloadFile(
            `/employees/${empId}/settlement/excel?start=${empData.start}&end=${empData.end}`,
            `report_${(empName || "labourer").replace(/\s+/g, "_")}_${empData.start}_to_${empData.end}.xlsx`
          )} excelLabel="Export Excel"
          pdfFileName={`report_${(empName || "labourer").replace(/\s+/g, "_")}_${empData.start}_to_${empData.end}.pdf`}>
          <SettlementSlipBody name={empName} data={empData} title="Labour Report" />
        </PrintableStatement>
      )}
    </div>
  );
}